import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  computed,
  signal,
} from '@angular/core';

import { DOCUMENT } from '@angular/common';

export type SlotStatus = 'available' | 'assigned' | 'inactive';

export interface CardSlot {
  id: number | string;
  label: string;
  status: SlotStatus;
}

@Component({
  selector: 'app-card-inventory',
  standalone: true,
  imports: [],
  templateUrl: './card-inventory.component.html',
  styleUrl: './card-inventory.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardInventoryComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Slots to render. */
  @Input() set slots(value: CardSlot[]) {
    this._slots.set(value ?? []);
  }

  /** Optional: pre-selected slot id. */
  @Input() set selectedId(value: CardSlot['id'] | null) {
    this.selected.set(value);
    this.initialSelected = value;
  }

  /** Minimum number of boxes to show. Empty ones are shown for missing cards. */
  @Input() set totalSlots(value: number) {
    this._total.set(value > 0 ? value : 0);
  }

  @Input() title = 'Assign Card';

  /** Text of the main button: "Assign" or "Swap". */
  @Input() confirmLabel = 'Assign';

  /** Disables the main button while your API call is running. */
  @Input() loading = false;

  /** Emits the chosen slot when the main button is pressed. */
  @Output() assigned = new EventEmitter<CardSlot>();

  /** Emits when the popup is closed (X, backdrop, Esc). */
  @Output() closed = new EventEmitter<void>();

  @ViewChild('dialog', { static: true }) dialogRef!: ElementRef<HTMLElement>;

  private readonly _slots = signal<CardSlot[]>([]);
  private readonly _total = signal(50);
  readonly selected = signal<CardSlot['id'] | null>(null);
  private initialSelected: CardSlot['id'] | null = null;

  readonly slotList = this._slots.asReadonly();

  /** Real cards first, then empty (null) boxes up to totalSlots. */
  readonly displaySlots = computed<(CardSlot | null)[]>(() => {
    const list = this._slots();
    const emptyCount = Math.max(this._total() - list.length, 0);
    return [...list, ...Array<null>(emptyCount).fill(null)];
  });

  readonly selectedSlot = computed(
    () => this._slots().find((s) => s.id === this.selected()) ?? null,
  );

  constructor(@Inject(DOCUMENT) private document: Document) {}

  ngOnInit(): void {
    // lock background scroll while popup is open
    this.document.body.classList.add('ac-no-scroll');
  }

  ngAfterViewInit(): void {
    this.dialogRef.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.document.body.classList.remove('ac-no-scroll');
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  toggle(slot: CardSlot): void {
    if (slot.status !== 'available') return;
    this.selected.set(this.selected() === slot.id ? null : slot.id);
  }

  reset(): void {
    this.selected.set(this.initialSelected);
  }

  assign(): void {
    const slot = this.selectedSlot();
    if (slot && !this.loading) this.assigned.emit(slot);
  }

  close(): void {
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.close();
  }
}